import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const AssetListLayer = () => {
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [perPage, setPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [pagination, setPagination] = useState({
        current_page: 1,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0,
    });

    // View modal state
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingAsset, setViewingAsset] = useState(null);

    // Edit modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingAsset, setEditingAsset] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [editPicture, setEditPicture] = useState(null);
    const [editPicturePreview, setEditPicturePreview] = useState('');
    const [editError, setEditError] = useState('');
    const [editValidationErrors, setEditValidationErrors] = useState({});
    const [isUpdating, setIsUpdating] = useState(false);

    const fetchAssets = async (page = 1) => {
        try {
            setLoading(true);
            setError('');

            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            if (!apiUrl || !token) {
                throw new Error('Missing API URL or authentication token');
            }

            const params = new URLSearchParams({
                per_page: perPage,
                page: page,
            });

            if (searchQuery) {
                params.append('search', searchQuery);
            }

            const response = await fetch(`${apiUrl}/assets?${params}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch assets: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setAssets(data.data);
                setPagination(data.pagination);
            } else {
                throw new Error(data.message || 'Failed to fetch assets');
            }
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : 'Unexpected error');
        } finally {
            setLoading(false);
        }
    };

    const handleViewClick = async (assetId) => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const response = await fetch(`${apiUrl}/assets/${assetId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (data.success) {
                setViewingAsset(data.data);
                setShowViewModal(true);
            }
        } catch (err) {
            console.error(err);
            alert('Failed to load asset details');
        }
    };

    const handleEditClick = async (assetId) => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const response = await fetch(`${apiUrl}/assets/${assetId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (data.success) {
                setEditingAsset(data.data);
                setEditFormData(data.data);
                setEditPicture(null);
                setEditPicturePreview(data.data.picture_url || '');
        setEditError('');
        setEditValidationErrors({});
        setShowEditModal(true);
            }
        } catch (err) {
            console.error(err);
            alert('Failed to load asset details');
        }
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (editValidationErrors[name]) {
            setEditValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleEditPictureChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setEditPicture(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setEditPicturePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setEditError('');
        setEditValidationErrors({});

        const apiUrl = process.env.REACT_APP_API_URL;
        const token = localStorage.getItem('authToken');

        try {
            setIsUpdating(true);

            // Use FormData if there's a new picture, otherwise use JSON
            let response;
            if (editPicture) {
                // If uploading a new picture, use FormData with POST and _method
                const formData = new FormData();
                
                // Append all form fields
                Object.keys(editFormData).forEach(key => {
                    if (editFormData[key] !== null && editFormData[key] !== undefined && key !== 'picture' && key !== 'picture_url') {
                        formData.append(key, editFormData[key]);
                    }
                });

                // Append the new picture
                formData.append('picture', editPicture);
                formData.append('_method', 'PUT');

                response = await fetch(`${apiUrl}/assets/${editingAsset.id}`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                    body: formData,
                });
            } else {
                // No new picture, just update other fields with JSON
                const updateData = { ...editFormData };
                delete updateData.picture;
                delete updateData.picture_url;

                response = await fetch(`${apiUrl}/assets/${editingAsset.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(updateData),
            });
            }

            const data = await response.json();

            if (!response.ok) {
                if (data.errors) {
                    setEditValidationErrors(data.errors);
                }
                throw new Error(data.message || 'Failed to update asset');
            }

            if (data.success) {
                setShowEditModal(false);
                fetchAssets(pagination.current_page);
            }
        } catch (err) {
            console.error(err);
            setEditError(err instanceof Error ? err.message : 'Unexpected error');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDelete = async (assetId) => {
        if (!window.confirm('Are you sure you want to delete this asset?')) {
            return;
        }

        try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const token = localStorage.getItem('authToken');

            const response = await fetch(`${apiUrl}/assets/${assetId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (data.success) {
                fetchAssets(pagination.current_page);
            } else {
                alert(data.message || 'Failed to delete asset');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to delete asset');
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchAssets(1);
    };

    useEffect(() => {
        fetchAssets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [perPage]);

    useEffect(() => {
        const handleEscapeKey = (e) => {
            if (e.key === 'Escape') {
                if (showViewModal) {
                    setShowViewModal(false);
                } else if (showEditModal) {
                    setShowEditModal(false);
                }
            }
        };

        document.addEventListener('keydown', handleEscapeKey);
        return () => document.removeEventListener('keydown', handleEscapeKey);
    }, [showViewModal, showEditModal]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const getConditionBadge = (condition) => {
        const badges = {
            excellent: 'bg-success-focus text-success-600 border border-success-main',
            good: 'bg-info-focus text-info-600 border border-info-main',
            fair: 'bg-warning-focus text-warning-600 border border-warning-main',
            poor: 'bg-danger-focus text-danger-600 border border-danger-main',
            damaged: 'bg-neutral-200 text-neutral-600 border border-neutral-400'
        };
        return badges[condition] || badges.good;
    };

    const renderPaginationButtons = () => {
        const buttons = [];
        const { current_page, last_page } = pagination;

        for (let i = 1; i <= last_page; i++) {
            buttons.push(
                <li className="page-item" key={i}>
                    <button
                        className={`page-link fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                            current_page === i
                                ? 'bg-primary-600 text-white'
                                : 'bg-neutral-200 text-secondary-light'
                        }`}
                        onClick={() => fetchAssets(i)}
                    >
                        {i}
                    </button>
                </li>
            );
        }
        return buttons;
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <div className="d-flex align-items-center flex-wrap gap-3">
                    <span className="text-md fw-medium text-secondary-light mb-0">Show</span>
                    <select
                        className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
                        value={perPage}
                        onChange={(e) => setPerPage(Number(e.target.value))}
                    >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                    </select>
                    <form className="navbar-search" onSubmit={handleSearch}>
                        <input
                            type="text"
                            className="bg-base h-40-px w-auto"
                            name="search"
                            placeholder="Search assets..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <Icon icon="ion:search-outline" className="icon" />
                    </form>
                </div>
                <Link
                    to="/add-asset"
                    className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                >
                    <Icon
                        icon="ic:baseline-plus"
                        className="icon text-xl line-height-1"
                    />
                    Add New Asset
                </Link>
            </div>
            <div className="card-body p-24">
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <>
                <div className="table-responsive scroll-sm">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr>
                                        <th scope="col">S.L</th>
                                        <th scope="col">Item Name</th>
                                        <th scope="col">Tagging Number</th>
                                        <th scope="col">Category</th>
                                        <th scope="col">Serial Number</th>
                                        <th scope="col">Location</th>
                                        <th scope="col">Holder/User</th>
                                        <th scope="col">Date Acquired</th>
                                        <th scope="col">Original Cost</th>
                                <th scope="col" className="text-center">
                                            Condition
                                </th>
                                <th scope="col" className="text-center">
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                                    {assets.length === 0 ? (
                                        <tr>
                                            <td colSpan="11" className="text-center py-4">
                                                No assets found
                                </td>
                            </tr>
                                    ) : (
                                        assets.map((asset, index) => (
                                            <tr key={asset.id}>
                                                <td>
                                                    {(pagination.current_page - 1) * perPage + index + 1}
                                </td>
                                <td>
                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {asset.item_name}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="badge bg-primary-50 text-primary-600 px-12 py-4 radius-4 fw-medium text-sm">
                                                        {asset.tagging_number}
                                    </span>
                                </td>
                                <td>
                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {asset.asset_category}
                                    </span>
                                </td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {asset.serial_number || 'N/A'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {asset.location || 'N/A'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {asset.holder_user || 'N/A'}
                                                    </span>
                                                </td>
                                                <td>{formatDate(asset.date_acquired)}</td>
                                                <td>
                                                    <span className="fw-semibold">
                                                        {formatCurrency(asset.original_cost)}
                                    </span>
                                </td>
                                <td className="text-center">
                                                    <span
                                                        className={`${getConditionBadge(
                                                            asset.condition
                                                        )} px-24 py-4 radius-4 fw-medium text-sm text-capitalize`}
                                                    >
                                                        {asset.condition}
                                    </span>
                                </td>
                                <td className="text-center">
                                    <div className="d-flex align-items-center gap-10 justify-content-center">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleViewClick(asset.id)}
                                            className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                            title="View Details"
                                        >
                                            <Icon
                                                icon="majesticons:eye-line"
                                                className="icon text-xl"
                                            />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleEditClick(asset.id)}
                                                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                            title="Edit Asset"
                                                        >
                                                            <Icon icon="lucide:edit" className="menu-icon" />
                                                        </button>
                                        <button
                                            type="button"
                                                            onClick={() => handleDelete(asset.id)}
                                            className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                            title="Delete Asset"
                                        >
                                            <Icon
                                                icon="fluent:delete-24-regular"
                                                className="menu-icon"
                                            />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                                        ))
                                    )}
                        </tbody>
                    </table>
                </div>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                            <span>
                                Showing {pagination.from || 0} to {pagination.to || 0} of{' '}
                                {pagination.total} entries
                            </span>
                    <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
                        <li className="page-item">
                                    <button
                                        className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                        onClick={() => fetchAssets(pagination.current_page - 1)}
                                        disabled={pagination.current_page === 1}
                            >
                                <Icon icon="ep:d-arrow-left" className="" />
                                    </button>
                        </li>
                                {renderPaginationButtons()}
                        <li className="page-item">
                                    <button
                                        className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                        onClick={() => fetchAssets(pagination.current_page + 1)}
                                        disabled={pagination.current_page === pagination.last_page}
                                    >
                                        <Icon icon="ep:d-arrow-right" className="" />
                                    </button>
                        </li>
                    </ul>
                </div>
                    </>
                )}
            </div>

            {/* View Asset Modal */}
            {showViewModal && viewingAsset && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Asset Details</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowViewModal(false)}
                                />
                            </div>
                            <div className="modal-body">
                                {viewingAsset.picture_url && (
                                    <div className="text-center mb-4">
                                        <img
                                            src={viewingAsset.picture_url}
                                            alt={viewingAsset.item_name}
                                            className="img-fluid rounded"
                                            style={{ maxHeight: '400px', objectFit: 'contain' }}
                                        />
                                    </div>
                                )}
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <strong>Item Name:</strong> {viewingAsset.item_name}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Tagging Number:</strong> {viewingAsset.tagging_number}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Category:</strong> {viewingAsset.asset_category}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Serial Number:</strong> {viewingAsset.serial_number || 'N/A'}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Location:</strong> {viewingAsset.location || 'N/A'}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Department:</strong> {viewingAsset.department || 'N/A'}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Holder/User:</strong> {viewingAsset.holder_user || 'N/A'}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Condition:</strong>{' '}
                                        <span className={`badge ${getConditionBadge(viewingAsset.condition)} text-capitalize`}>
                                            {viewingAsset.condition}
                                        </span>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Date Acquired:</strong> {formatDate(viewingAsset.date_acquired)}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Original Cost:</strong> {formatCurrency(viewingAsset.original_cost)}
                                    </div>
                                    <div className="col-12 mb-3">
                                        <strong>Description:</strong> {viewingAsset.description || 'N/A'}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowViewModal(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Asset Modal */}
            {showEditModal && editingAsset && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit Asset</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowEditModal(false)}
                                    disabled={isUpdating}
                                />
                            </div>
                            <form onSubmit={handleEditSubmit} noValidate>
                                <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                    {editError && (
                                        <div className="alert alert-danger" role="alert">
                                            {editError}
                                        </div>
                                    )}
                                    <div className="row">
                                        {/* Item Name */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Item Name *</label>
                                        <input
                                            type="text"
                                                className={`form-control ${editValidationErrors.item_name ? 'is-invalid' : ''}`}
                                                name="item_name"
                                                value={editFormData.item_name || ''}
                                            onChange={handleEditInputChange}
                                            required
                                        />
                                            {editValidationErrors.item_name && (
                                            <div className="invalid-feedback d-block">
                                                    {editValidationErrors.item_name[0]}
                                            </div>
                                        )}
                                    </div>

                                        {/* Tagging Number */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Tagging Number *</label>
                                        <input
                                            type="text"
                                                className={`form-control ${editValidationErrors.tagging_number ? 'is-invalid' : ''}`}
                                                name="tagging_number"
                                                value={editFormData.tagging_number || ''}
                                            onChange={handleEditInputChange}
                                            required
                                        />
                                            {editValidationErrors.tagging_number && (
                                            <div className="invalid-feedback d-block">
                                                    {editValidationErrors.tagging_number[0]}
                                            </div>
                                        )}
                                    </div>

                                        {/* Description */}
                                        <div className="col-12 mb-3">
                                            <label className="form-label fw-semibold">Description</label>
                                            <textarea
                                                className={`form-control ${editValidationErrors.description ? 'is-invalid' : ''}`}
                                                name="description"
                                                rows="3"
                                                value={editFormData.description || ''}
                                            onChange={handleEditInputChange}
                                        />
                                            {editValidationErrors.description && (
                                            <div className="invalid-feedback d-block">
                                                    {editValidationErrors.description[0]}
                                            </div>
                                        )}
                                    </div>

                                        {/* Picture Upload */}
                                        <div className="col-12 mb-3">
                                            <label className="form-label fw-semibold">Picture</label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                accept="image/*"
                                                onChange={handleEditPictureChange}
                                            />
                                            {editPicturePreview && (
                                                <div className="mt-2">
                                                    <img
                                                        src={editPicturePreview}
                                                        alt="Preview"
                                                        className="img-thumbnail"
                                                        style={{ maxWidth: '200px', maxHeight: '200px' }}
                                                    />
                                                </div>
                                            )}
                                            {editValidationErrors.picture && (
                                                <div className="text-danger mt-1">
                                                    {editValidationErrors.picture[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Asset Category */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Asset Category *</label>
                                        <select
                                                className={`form-control form-select ${editValidationErrors.asset_category ? 'is-invalid' : ''}`}
                                                name="asset_category"
                                                value={editFormData.asset_category || ''}
                                            onChange={handleEditInputChange}
                                            required
                                        >
                                                <option value="">Select Category</option>
                                                <option value="Vehicles">Vehicles</option>
                                                <option value="Equipments">Equipments</option>
                                                <option value="Furniture and fixture">Furniture and fixture</option>
                                        </select>
                                            {editValidationErrors.asset_category && (
                                            <div className="invalid-feedback d-block">
                                                    {editValidationErrors.asset_category[0]}
                                            </div>
                                        )}
                                    </div>

                                        {/* Serial Number */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Serial Number</label>
                                            <input
                                                type="text"
                                                className={`form-control ${editValidationErrors.serial_number ? 'is-invalid' : ''}`}
                                                name="serial_number"
                                                value={editFormData.serial_number || ''}
                                            onChange={handleEditInputChange}
                                            />
                                            {editValidationErrors.serial_number && (
                                            <div className="invalid-feedback d-block">
                                                    {editValidationErrors.serial_number[0]}
                                            </div>
                                        )}
                                    </div>

                                        {/* Location */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Location</label>
                                            <input
                                                type="text"
                                                className={`form-control ${editValidationErrors.location ? 'is-invalid' : ''}`}
                                                name="location"
                                                value={editFormData.location || ''}
                                                onChange={handleEditInputChange}
                                            />
                                            {editValidationErrors.location && (
                                                <div className="invalid-feedback d-block">
                                                    {editValidationErrors.location[0]}
                                </div>
                                            )}
                                </div>

                                        {/* Department */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Department</label>
                                            <input
                                                type="text"
                                                className={`form-control ${editValidationErrors.department ? 'is-invalid' : ''}`}
                                                name="department"
                                                value={editFormData.department || ''}
                                                onChange={handleEditInputChange}
                                            />
                                            {editValidationErrors.department && (
                                                <div className="invalid-feedback d-block">
                                                    {editValidationErrors.department[0]}
                        </div>
                                            )}
                    </div>

                                        {/* Holder/User */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Holder / User</label>
                                            <input
                                                type="text"
                                                className={`form-control ${editValidationErrors.holder_user ? 'is-invalid' : ''}`}
                                                name="holder_user"
                                                value={editFormData.holder_user || ''}
                                                onChange={handleEditInputChange}
                                            />
                                            {editValidationErrors.holder_user && (
                                                <div className="invalid-feedback d-block">
                                                    {editValidationErrors.holder_user[0]}
                </div>
            )}
                                        </div>

                                        {/* Useful Life */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Useful Life (Years)</label>
                                            <input
                                                type="number"
                                                className={`form-control ${editValidationErrors.useful_life ? 'is-invalid' : ''}`}
                                                name="useful_life"
                                                value={editFormData.useful_life || ''}
                                                onChange={handleEditInputChange}
                                            />
                                            {editValidationErrors.useful_life && (
                                                <div className="invalid-feedback d-block">
                                                    {editValidationErrors.useful_life[0]}
                            </div>
                                            )}
                                        </div>

                                        {/* Date Acquired */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Date Acquired</label>
                                            <input
                                                type="date"
                                                className={`form-control ${editValidationErrors.date_acquired ? 'is-invalid' : ''}`}
                                                name="date_acquired"
                                                value={editFormData.date_acquired || ''}
                                                onChange={handleEditInputChange}
                                            />
                                            {editValidationErrors.date_acquired && (
                                                <div className="invalid-feedback d-block">
                                                    {editValidationErrors.date_acquired[0]}
                                        </div>
                                    )}
                                        </div>

                                        {/* Original Cost */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Original Cost</label>
                                        <input
                                                type="number"
                                                step="0.01"
                                                className={`form-control ${editValidationErrors.original_cost ? 'is-invalid' : ''}`}
                                                name="original_cost"
                                                value={editFormData.original_cost || ''}
                                                onChange={handleEditInputChange}
                                            />
                                            {editValidationErrors.original_cost && (
                                            <div className="invalid-feedback d-block">
                                                    {editValidationErrors.original_cost[0]}
                                            </div>
                                        )}
                                    </div>

                                        {/* Net Book Value */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Net Book Value</label>
                                        <input
                                                type="number"
                                                step="0.01"
                                                className={`form-control ${editValidationErrors.net_book_value ? 'is-invalid' : ''}`}
                                                name="net_book_value"
                                                value={editFormData.net_book_value || ''}
                                                onChange={handleEditInputChange}
                                            />
                                            {editValidationErrors.net_book_value && (
                                            <div className="invalid-feedback d-block">
                                                    {editValidationErrors.net_book_value[0]}
                                            </div>
                                        )}
                                    </div>

                                        {/* Running Date */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Running Date</label>
                                        <input
                                                type="date"
                                                className={`form-control ${editValidationErrors.running_date ? 'is-invalid' : ''}`}
                                                name="running_date"
                                                value={editFormData.running_date || ''}
                                                onChange={handleEditInputChange}
                                            />
                                            {editValidationErrors.running_date && (
                                            <div className="invalid-feedback d-block">
                                                    {editValidationErrors.running_date[0]}
                                            </div>
                                        )}
                                    </div>

                                        {/* Retirement Date */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Retirement Date</label>
                                            <input
                                                type="date"
                                                className={`form-control ${editValidationErrors.retirement_date ? 'is-invalid' : ''}`}
                                                name="retirement_date"
                                                value={editFormData.retirement_date || ''}
                                                onChange={handleEditInputChange}
                                            />
                                            {editValidationErrors.retirement_date && (
                                            <div className="invalid-feedback d-block">
                                                    {editValidationErrors.retirement_date[0]}
                                            </div>
                                        )}
                                    </div>

                                        {/* Accumulated Depreciation */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Accumulated Depreciation</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className={`form-control ${editValidationErrors.accumulated_depreciation ? 'is-invalid' : ''}`}
                                                name="accumulated_depreciation"
                                                value={editFormData.accumulated_depreciation || ''}
                                                onChange={handleEditInputChange}
                                            />
                                            {editValidationErrors.accumulated_depreciation && (
                                                <div className="invalid-feedback d-block">
                                                    {editValidationErrors.accumulated_depreciation[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Current Month's Depreciation */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Current Month's Depreciation</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className={`form-control ${editValidationErrors.current_month_depreciation ? 'is-invalid' : ''}`}
                                                name="current_month_depreciation"
                                                value={editFormData.current_month_depreciation || ''}
                                                onChange={handleEditInputChange}
                                            />
                                            {editValidationErrors.current_month_depreciation && (
                                                <div className="invalid-feedback d-block">
                                                    {editValidationErrors.current_month_depreciation[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Yearly Depreciation */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Yearly Depreciation</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className={`form-control ${editValidationErrors.yearly_depreciation ? 'is-invalid' : ''}`}
                                                name="yearly_depreciation"
                                                value={editFormData.yearly_depreciation || ''}
                                                onChange={handleEditInputChange}
                                            />
                                            {editValidationErrors.yearly_depreciation && (
                                                <div className="invalid-feedback d-block">
                                                    {editValidationErrors.yearly_depreciation[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Full Depreciation */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Full Depreciation</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className={`form-control ${editValidationErrors.full_depreciation ? 'is-invalid' : ''}`}
                                                name="full_depreciation"
                                                value={editFormData.full_depreciation || ''}
                                                onChange={handleEditInputChange}
                                            />
                                            {editValidationErrors.full_depreciation && (
                                                <div className="invalid-feedback d-block">
                                                    {editValidationErrors.full_depreciation[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Additions */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Additions</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className={`form-control ${editValidationErrors.additions ? 'is-invalid' : ''}`}
                                                name="additions"
                                                value={editFormData.additions || ''}
                                                onChange={handleEditInputChange}
                                            />
                                            {editValidationErrors.additions && (
                                                <div className="invalid-feedback d-block">
                                                    {editValidationErrors.additions[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Disposals */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Disposals</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className={`form-control ${editValidationErrors.disposals ? 'is-invalid' : ''}`}
                                                name="disposals"
                                                value={editFormData.disposals || ''}
                                                onChange={handleEditInputChange}
                                            />
                                            {editValidationErrors.disposals && (
                                                <div className="invalid-feedback d-block">
                                                    {editValidationErrors.disposals[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Condition */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Condition</label>
                                        <select
                                                className={`form-control form-select ${editValidationErrors.condition ? 'is-invalid' : ''}`}
                                                name="condition"
                                                value={editFormData.condition || ''}
                                                onChange={handleEditInputChange}
                                            >
                                                <option value="excellent">Excellent</option>
                                                <option value="good">Good</option>
                                                <option value="fair">Fair</option>
                                                <option value="poor">Poor</option>
                                                <option value="damaged">Damaged</option>
                                        </select>
                                            {editValidationErrors.condition && (
                                            <div className="invalid-feedback d-block">
                                                    {editValidationErrors.condition[0]}
                                            </div>
                                        )}
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowEditModal(false)}
                                        disabled={isUpdating}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={isUpdating}
                                    >
                                        {isUpdating ? 'Updating...' : 'Update Asset'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AssetListLayer;
